import { Link, useNavigate } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import BuyerInfoModal from "../../components/modals/BuyerInfoModal";
import ProgressBar from "../../components/widgets/ProgressBar";
import ClientDocuments from "../../components/documents/ClientDocuments";
import { useModal } from "../../js/hooks/useModal";
import { useAuth } from "../../js/hooks/useAuth";
import { useGuestMode } from "../../js/hooks/useGuestMode";
import { useStore } from "../../js/state/store";
import { preferenceProgress } from "../../js/utils/formatters";
import { updateBuyer } from "../../js/api/buyersApi";
import DeskPlatformBadge from "../../components/desk/DeskPlatformBadge";

export default function BuyerStart() {
  const navigate = useNavigate();
  const accountModal = useModal("buyerAccount");
  const infoModal = useModal("buyerInfo");
  const { session } = useAuth();
  const { isViewOnly, openGuestLock } = useGuestMode();
  const { state, dispatch } = useStore();
  const progress = preferenceProgress(state.buyer.current.preferences);

  const hasBuyerAccount = session?.role === "buyer" || session?.role === "admin";

  async function handleInfo(form) {
    if (isViewOnly) {
      openGuestLock();
      return;
    }
    const next = {
      ...state.buyer.current,
      preferences: {
        ...state.buyer.current.preferences,
        location: {
          ...state.buyer.current.preferences.location,
          timeline: form.timeline,
          currentHousing: form.currentHousing,
          householdSize: form.householdSize,
          budget: form.budget,
        },
      },
    };
    dispatch({ type: "SET_BUYER", payload: next });
    if (next.id) await updateBuyer(next.id, next);
    infoModal.close();
    navigate("/location");
  }

  function beginChecklist() {
    if (isViewOnly) {
      openGuestLock();
      return;
    }
    const location = state.buyer.current.preferences.location || {};
    if (!location.timeline) {
      infoModal.open();
      return;
    }
    navigate("/location");
  }

  return (
    <AppLayout variant="buyer" showBuyerNav>
      <section className="hero-panel" data-tour="buyer-hero">
        <DeskPlatformBadge />
        <h1>{session?.role === "buyer" ? "Buyer Dashboard" : "Welcome to Your DreamHome Checklist"}</h1>
        <p>
          This is the conversation starter your agent can actually use. Walk through each
          section at your own pace. Your answers become a clear, organized brief — so showings,
          searches, and offers stay aligned with what you want, not a guess from a rushed call.
        </p>
      </section>
      <div className="row start-actions">
        <button type="button" className="pill-btn start-cta" onClick={accountModal.open}>
          {session ? "Update buyer account" : "Sign in"}
        </button>
        <button
          type="button"
          className={`pill-btn start-cta start-cta--begin${hasBuyerAccount ? " is-active" : ""}`}
          onClick={beginChecklist}
        >
          Begin Checklist
        </button>
        {hasBuyerAccount ? (
          <Link to="/client/messages" className="ghost-btn">
            Messages with your agent
          </Link>
        ) : null}
      </div>

      <div className="card stack">
        <section>
          <h2>How the Process Works</h2>
          <ol className="process-list" data-tour="buyer-process">
            <li>Sign in so your agent can match this checklist to you.</li>
            <li>
              Complete each DreamHome section: location, home type, lot, exterior, interior,
              lifestyle, and accessibility.
            </li>
            <li>Add special notes, deal-breakers, and anything unique to your household.</li>
            <li>Review buyer tips, then track your transaction from pre-approval to walkthrough.</li>
            <li>Submit the finished checklist to your agent from the last page.</li>
          </ol>
        </section>

        <section>
          <h2>Why This Helps You and Your Agent</h2>
          <p className="page-lead">
            Buyers often describe a feeling (“something with character,” “not too busy,” “good
            area”) that is hard to search. This checklist turns those instincts into specific,
            Fair Housing–safe preferences — commute time, lot type, kitchen layout, accessibility
            needs, and more. Your agent can filter listings, plan tours, and write offers with
            fewer back-and-forth emails and fewer houses that were never going to fit.
          </p>
        </section>

        <ProgressBar percent={progress.percent} label="Your saved progress" />
        {hasBuyerAccount ? (
          <p className="muted" style={{ marginTop: "0.75rem" }}>
            <Link to="/backup">Back up your checklist</Link>
            {" · "}
            <Link to="/restore">Restore an earlier version</Link>
          </p>
        ) : null}
      </div>

      <ClientDocuments ownerId={hasBuyerAccount ? session?.userId : ""} role="buyer" />

      <BuyerInfoModal
        isOpen={infoModal.isOpen}
        onClose={infoModal.close}
        onSubmit={handleInfo}
        initialValues={state.buyer.current.preferences.location}
      />
    </AppLayout>
  );
}
